import React from 'react'
import {
	ColorArea,
	type ColorAreaProps,
	ColorPicker,
	ColorSlider,
	type ColorSwatchProps,
	ColorThumb,
	type ColorThumbProps,
	SliderTrack,
	type SliderTrackProps,
	composeRenderProps,
	ColorSwatchPicker as AriaColorSwatchPicker,
	ColorSwatchPickerItem as AriaColorSwatchPickerItem,
	ColorSwatch as AriaColorSwatch,
	type ColorSwatchPickerItemProps,
	type ColorSwatchPickerProps,
} from 'react-aria-components'
import { twMerge } from 'tailwind-merge'
import { cn } from '#app/utils/misc.tsx'

export function composeTailwindRenderProps<T>(
	className: string | ((v: T) => string) | undefined,
	tw: string,
): string | ((v: T) => string) {
	return composeRenderProps(className, (className) => twMerge(tw, className))
}

const AriaColorSlider = ColorSlider

const AriaColorPicker = ColorPicker

function AriaColorArea({ className, ...props }: ColorAreaProps) {
	return (
		<ColorArea
			className={composeRenderProps(className, (className) =>
				cn('aspect-square w-full shrink-0 rounded-lg', className),
			)}
			{...props}
		/>
	)
}

function AriaColorSliderTrack({ className, ...props }: SliderTrackProps) {
	return (
		<SliderTrack
			className={composeRenderProps(className, (className) =>
				cn('h-9 rounded-lg', className),
			)}
			{...props}
		/>
	)
}

function AriaColorThumb({ className, ...props }: ColorThumbProps) {
	return (
		<ColorThumb
			className={composeRenderProps(className, (className) =>
				cn(
					'absolute top-1/2 z-50 box-border size-6 cursor-grab rounded-full shadow-md ring-2 ring-white ring-inset',
					/* Focus Visible */
					'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
					className,
				),
			)}
			{...props}
		/>
	)
}

export function ColorSwatch(props: ColorSwatchProps) {
	return (
		<AriaColorSwatch
			{...props}
			className={composeTailwindRenderProps(
				props.className,
				'box-border h-8 w-8 rounded-md border border-black/10',
			)}
			style={({ color }) => ({
				background: `linear-gradient(${color}, ${color}),
			repeating-conic-gradient(#CCC 0% 25%, white 0% 50%) 50% / 16px 16px`,
			})}
		/>
	)
}

export function ColorSwatchPicker({
	children,
	...props
}: Omit<ColorSwatchPickerProps, 'layout'>) {
	return (
		<AriaColorSwatchPicker
			{...props}
			className={composeRenderProps(props.className, (className) =>
				cn('flex flex-wrap gap-1', className),
			)}
		>
			{children}
		</AriaColorSwatchPicker>
	)
}

export function ColorSwatchPickerItem({
	className,
	...props
}: ColorSwatchPickerItemProps) {
	return (
		<AriaColorSwatchPickerItem
			{...props}
			className={({ isSelected, isFocusVisible, isHovered }) =>
				cn(
					'relative rounded-md outline-hidden [-webkit-tap-highlight-color:transparent]',
					isHovered && !isSelected && 'ring-border-primary ring-1',
					isSelected && 'ring-fg-brand-primary ring-2',
					isFocusVisible &&
						!isSelected &&
						'ring-focus-ring ring-offset-bg-primary ring-2 ring-offset-2',
					className,
				)
			}
		>
			<ColorSwatch />
		</AriaColorSwatchPickerItem>
	)
}

export {
	AriaColorSlider,
	AriaColorPicker,
	AriaColorArea,
	AriaColorSliderTrack,
	AriaColorThumb,
	AriaColorSwatch,
}
